import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Typography,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { CreditCard, LocalAtm, ChevronRight } from "@material-ui/icons";
import paymentApi from "../../api/payment";

export type GamePaymentSummaryData = {
  paymentCount: number;
  playersPaidCount: number;
  unpaidPlayerCount: number;
  guestPlayerCount: number;
  grossCollectedPence: number;
  schalaFeePence: number;
  stripeProcessingPence: number;
  takeHomePence: number;
  manualCollectedPence: number;
  cardGrossPence: number;
  cardTakeHomePence: number;
  stripePaymentCount: number;
  manualPaymentCount: number;
};

const formatGbp = (pence: number) => `£${(pence / 100).toFixed(2)}`;

const mono = '"Roboto Mono", "SFMono-Regular", Menlo, monospace';

type Props = {
  gameId: string;
  refreshKey?: number;
  /** Show full ledger without expand/collapse — for Received payments dialog. */
  alwaysExpanded?: boolean;
};

type ArithmeticOp = "+" | "−" | "=";

type ArithmeticLine = {
  op: ArithmeticOp;
  label: string;
  entryAmountPence?: number;
  multiplier?: number;
  value: string;
  isTotal?: boolean;
  isDeduction?: boolean;
  isPlaceholder?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  rootDialog: {
    border: "none",
    borderRadius: 0,
    width: "100%",
    maxWidth: "none",
    margin: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    boxSizing: "border-box",
  },
  summaryTrigger: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
    width: "100%",
    padding: theme.spacing(2.5, 2.5),
    cursor: "pointer",
    userSelect: "none",
    transition: "background-color 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
    "&:active": {
      transform: "scale(0.998)",
    },
    "&:focus": {
      outline: "none",
    },
    "&:focus-visible": {
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
    },
  },
  summaryTriggerExpanded: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  summaryHeaderStatic: {
    width: "100%",
    padding: theme.spacing(2.5, 2.5, 2.25),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  summaryHeaderStaticDialog: {
    [theme.breakpoints.up(501)]: {
      padding: theme.spacing(3, 3.5, 2.75),
    },
  },
  summaryHeroBlockDialog: {
    [theme.breakpoints.up(501)]: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      alignItems: "center",
      columnGap: theme.spacing(4),
    },
  },
  summaryRosterDialog: {
    [theme.breakpoints.up(501)]: {
      marginTop: 0,
    },
  },
  summaryHeroBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(0.75),
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
  },
  summaryHeroLead: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    width: "100%",
    minWidth: 0,
  },
  summaryHeroCaptionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  summaryCaption: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: theme.palette.grey[600],
    lineHeight: 1.35,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  summaryAmount: {
    fontSize: "2rem",
    fontWeight: 700,
    fontFamily: mono,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.primary.main,
    letterSpacing: "-0.03em",
    lineHeight: 1,
    margin: 0,
    whiteSpace: "nowrap",
  },
  summaryAmountMuted: {
    color: theme.palette.grey[500],
  },
  summaryRoster: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.875),
    marginTop: theme.spacing(1.25),
  },
  summaryToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.375),
    flexShrink: 0,
    color: theme.palette.grey[700],
    transition: "color 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
    "&:hover": {
      color: theme.palette.grey[900],
    },
  },
  summaryToggleText: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "inherit",
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  summaryChevron: {
    fontSize: 20,
    color: "inherit",
    flexShrink: 0,
    transformOrigin: "center",
    transition: "transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  summaryChevronExpanded: {
    transform: "rotate(90deg)",
  },
  expandedContent: {
    opacity: 1,
    transition: "opacity 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  expandedContentDialog: {
    [theme.breakpoints.up(501)]: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.grey[50],
    },
  },
  section: {
    padding: theme.spacing(2.25, 2.5),
  },
  sectionDialog: {
    [theme.breakpoints.up(501)]: {
      padding: theme.spacing(2.5, 3.5),
      flex: "0 0 auto",
    },
  },
  arithmetic: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.875),
  },
  arithmeticRow: {
    display: "grid",
    gridTemplateColumns: "20px 1fr minmax(76px, auto)",
    alignItems: "center",
    columnGap: theme.spacing(1.5),
    minHeight: 30,
  },
  arithmeticRowTotal: {
    display: "grid",
    gridTemplateColumns: "20px 1fr minmax(76px, auto)",
    alignItems: "center",
    columnGap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.75),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    minHeight: 34,
  },
  op: {
    fontFamily: mono,
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.grey[600],
    textAlign: "center",
    lineHeight: 1,
    justifySelf: "center",
  },
  opDeduction: {
    color: "#92400e",
  },
  opTotal: {
    color: theme.palette.primary.main,
  },
  lineLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.grey[800],
    lineHeight: 1.3,
  },
  lineLabelWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  lineDetailChip: {
    height: 22,
    fontSize: "0.6875rem",
    fontWeight: 700,
    fontFamily: mono,
    color: theme.palette.grey[800],
    backgroundColor: theme.palette.grey[200],
    border: "none",
    borderRadius: 4,
    flexShrink: 0,
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      lineHeight: 1.2,
    },
  },
  lineLabelTotal: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.grey[900],
    letterSpacing: "-0.01em",
  },
  lineValue: {
    fontSize: "0.875rem",
    fontWeight: 700,
    fontFamily: mono,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.grey[900],
    textAlign: "right",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  lineValueDeduction: {
    color: "#92400e",
  },
  lineValueTotal: {
    fontSize: "1.1875rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    letterSpacing: "-0.02em",
  },
  lineValueMuted: {
    color: theme.palette.grey[500],
  },
  lineLabelMuted: {
    color: theme.palette.grey[600],
    fontWeight: 500,
  },
  opMuted: {
    color: theme.palette.grey[400],
  },
  chipsBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    boxSizing: "border-box",
  },
  chipsBarDialog: {
    [theme.breakpoints.up(501)]: {
      padding: theme.spacing(1.75, 3.5),
      flex: "0 0 auto",
    },
  },
  chipItem: {
    flex: "0 0 auto",
    display: "flex",
    maxWidth: "100%",
  },
  chipItemLead: {
    flex: "1 1 0",
    minWidth: 72,
    display: "flex",
  },
  chipSep: {
    flexShrink: 0,
    fontFamily: mono,
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.grey[600],
    lineHeight: 1,
    padding: theme.spacing(0, 0.125),
    userSelect: "none",
  },
  chip: {
    height: 34,
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: theme.palette.grey[900],
    backgroundColor: "#fff",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      overflow: "visible",
      whiteSpace: "nowrap",
    },
    "& .MuiChip-icon": {
      color: theme.palette.grey[800],
      marginLeft: theme.spacing(0.75),
      marginRight: theme.spacing(-0.25),
    },
  },
  chipFill: {
    width: "100%",
    justifyContent: "center",
  },
  chipMuted: {
    color: theme.palette.grey[700],
    backgroundColor: theme.palette.grey[50],
    borderColor: theme.palette.grey[300],
    fontWeight: 600,
  },
  playerBarTrack: {
    width: "100%",
    padding: 3,
    borderRadius: 999,
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.06)",
    boxSizing: "border-box",
  },
  playerBar: {
    display: "flex",
    width: "100%",
    height: 8,
    borderRadius: 999,
    overflow: "hidden",
    gap: 2,
  },
  playerBarSegment: {
    minWidth: 6,
    borderRadius: 999,
    transition: "flex-grow 0.45s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  playerBarPaid: {
    backgroundColor: "#64748b",
  },
  playerBarGuest: {
    backgroundColor: "#94a3b8",
  },
  playerBarUnpaid: {
    backgroundColor: "#ef4444",
  },
  playerLegendRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.25, 2),
  },
  playerLegendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  playerLegendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  playerLegendCount: {
    fontFamily: mono,
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.8125rem",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  playerLegendCountPaid: {
    color: "#64748b",
  },
  playerLegendCountGuest: {
    color: "#94a3b8",
  },
  playerLegendCountUnpaid: {
    color: "#ef4444",
  },
  playerLegendLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.grey[700],
    lineHeight: 1,
  },
  playerLegendLabelGuest: {
    color: "#94a3b8",
  },
  playerLegendLabelUnpaid: {
    color: "#ef4444",
  },
  playerLegendSingle: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.grey[700],
    lineHeight: 1.4,
    margin: 0,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.625),
    padding: theme.spacing(1.75, 2.5, 2),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  footerDialog: {
    [theme.breakpoints.up(501)]: {
      marginTop: "auto",
      padding: theme.spacing(2, 3.5, 2.5),
      backgroundColor: "#fff",
      flex: "0 0 auto",
    },
  },
  footerText: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.4,
    color: theme.palette.grey[700],
    margin: 0,
    flex: 1,
  },
  stateBox: {
    width: "100%",
    padding: theme.spacing(2.25, 2.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
  },
  stateBoxDialog: {
    border: "none",
    borderRadius: 0,
    backgroundColor: "transparent",
    width: "100%",
    maxWidth: "none",
    margin: 0,
  },
  skeletonBar: {
    height: 10,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    animation: "$pulse 1.2s ease-in-out infinite",
  },
  skeletonBarWide: {
    height: 22,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    animation: "$pulse 1.2s ease-in-out infinite",
    marginTop: theme.spacing(1),
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.45 },
  },
}));

const perEntryPence = (totalPence: number, count: number) =>
  count > 0 ? Math.round(totalPence / count) : 0;

function hasCollections(summary: GamePaymentSummaryData): boolean {
  return (
    summary.takeHomePence > 0 ||
    summary.stripePaymentCount > 0 ||
    summary.manualPaymentCount > 0 ||
    summary.cardGrossPence > 0 ||
    summary.manualCollectedPence > 0
  );
}

function buildArithmeticLines(summary: GamePaymentSummaryData): ArithmeticLine[] {
  const collected = hasCollections(summary);
  const lines: ArithmeticLine[] = [];
  const hasManual = summary.manualPaymentCount > 0 && summary.manualCollectedPence > 0;
  const hasCard = summary.stripePaymentCount > 0 && summary.cardGrossPence > 0;
  const feesPence = summary.schalaFeePence + summary.stripeProcessingPence;
  const hasFees = feesPence > 0;

  if (!collected) {
    return [
      { op: "+", label: "Card", value: formatGbp(0), isPlaceholder: true },
      { op: "+", label: "Manual", value: formatGbp(0), isPlaceholder: true },
      {
        op: "=",
        label: "Net available",
        value: formatGbp(0),
        isTotal: true,
        isPlaceholder: true,
      },
    ];
  }

  if (hasManual) {
    lines.push({
      op: "+",
      label: "Manual",
      entryAmountPence: perEntryPence(summary.manualCollectedPence, summary.manualPaymentCount),
      multiplier: summary.manualPaymentCount,
      value: formatGbp(summary.manualCollectedPence),
    });
  }
  if (hasCard) {
    lines.push({
      op: "+",
      label: "Card",
      entryAmountPence: perEntryPence(summary.cardGrossPence, summary.stripePaymentCount),
      multiplier: summary.stripePaymentCount,
      value: formatGbp(summary.cardGrossPence),
    });
  }
  if (hasFees) {
    lines.push({
      op: "−",
      label: "Fees",
      value: formatGbp(feesPence),
      isDeduction: true,
    });
  }

  lines.push({
    op: "=",
    label: "Net available",
    value: formatGbp(summary.takeHomePence),
    isTotal: true,
  });

  return lines;
}

function SummaryLoading({ dialog = false }: { dialog?: boolean }) {
  const classes = useStyles();
  return (
    <Box className={`${classes.stateBox} ${dialog ? classes.stateBoxDialog : ""}`}>
      <Box className={classes.skeletonBar} width="40%" />
      <Box className={classes.skeletonBarWide} width="55%" />
    </Box>
  );
}

function ChipSeparator({ symbol }: { symbol: "=" | "+" }) {
  const classes = useStyles();
  return (
    <Typography component="span" className={classes.chipSep} aria-hidden>
      {symbol}
    </Typography>
  );
}

type PlayerBarSegment = {
  key: "paid" | "guest" | "unpaid";
  count: number;
  label: string;
  barClass: string;
  countClass: string;
  allLabel: string;
};

function buildPlayerBarSegments(classes: ReturnType<typeof useStyles>, summary: GamePaymentSummaryData) {
  const segments: PlayerBarSegment[] = [];

  if (summary.playersPaidCount > 0) {
    segments.push({
      key: "paid",
      count: summary.playersPaidCount,
      label: "paid",
      barClass: classes.playerBarPaid,
      countClass: classes.playerLegendCountPaid,
      allLabel: "all paid",
    });
  }
  if ((summary.guestPlayerCount ?? 0) > 0) {
    segments.push({
      key: "guest",
      count: summary.guestPlayerCount,
      label: "guest",
      barClass: classes.playerBarGuest,
      countClass: classes.playerLegendCountGuest,
      allLabel: "all guests",
    });
  }
  if (summary.unpaidPlayerCount > 0) {
    segments.push({
      key: "unpaid",
      count: summary.unpaidPlayerCount,
      label: "unpaid",
      barClass: classes.playerBarUnpaid,
      countClass: classes.playerLegendCountUnpaid,
      allLabel: "all unpaid",
    });
  }

  return segments;
}

function buildHeroCaption(totalPlayers: number, collected: boolean) {
  if (totalPlayers === 0) return collected ? "Net collected" : "Nothing collected yet";

  const roster = `${totalPlayers} player${totalPlayers === 1 ? "" : "s"} in game`;
  return collected ? `Net collected · ${roster}` : `Nothing collected yet · ${roster}`;
}

function PlayerRosterLegend({
  segments,
  classes,
}: {
  segments: PlayerBarSegment[];
  classes: ReturnType<typeof useStyles>;
}) {
  if (segments.length === 1) {
    return (
      <Typography className={classes.playerLegendSingle} component="p">
        {segments[0].allLabel}
      </Typography>
    );
  }

  return (
    <Box className={classes.playerLegendRow}>
      {segments.map(segment => (
        <Box key={segment.key} className={classes.playerLegendItem}>
          <Box className={`${classes.playerLegendDot} ${segment.barClass}`} aria-hidden />
          <Typography component="span" className={`${classes.playerLegendCount} ${segment.countClass}`}>
            {segment.count}
          </Typography>
          <Typography
            component="span"
            className={`${classes.playerLegendLabel}${
              segment.key === "guest"
                ? ` ${classes.playerLegendLabelGuest}`
                : segment.key === "unpaid"
                  ? ` ${classes.playerLegendLabelUnpaid}`
                  : ""
            }`}
          >
            {segment.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function SummaryHero({
  summary,
  classes,
  topRight,
  dialogMode = false,
}: {
  summary: GamePaymentSummaryData;
  classes: ReturnType<typeof useStyles>;
  topRight?: React.ReactNode;
  dialogMode?: boolean;
}) {
  const collected = hasCollections(summary);
  const segments = buildPlayerBarSegments(classes, summary);
  const totalPlayers = segments.reduce((sum, segment) => sum + segment.count, 0);
  const barLabel = segments.map(segment => `${segment.count} ${segment.label}`).join(", ");

  return (
    <Box
      className={`${classes.summaryHeroBlock}${dialogMode ? ` ${classes.summaryHeroBlockDialog}` : ""}`}
    >
      <Box className={classes.summaryHeroLead}>
        <Typography
          className={`${classes.summaryAmount}${collected ? "" : ` ${classes.summaryAmountMuted}`}`}
          component="p"
        >
          {formatGbp(summary.takeHomePence)}
        </Typography>

        <Box className={classes.summaryHeroCaptionRow}>
          <Typography className={classes.summaryCaption} component="p">
            {buildHeroCaption(totalPlayers, collected)}
          </Typography>
          {topRight ?? null}
        </Box>
      </Box>

      {totalPlayers > 0 ? (
        <Box className={`${classes.summaryRoster}${dialogMode ? ` ${classes.summaryRosterDialog}` : ""}`}>
          <Box className={classes.playerBarTrack}>
            <Box
              className={classes.playerBar}
              role="img"
              aria-label={`${totalPlayers} players: ${barLabel}`}
            >
              {segments.map(segment => (
                <Box
                  key={segment.key}
                  className={`${classes.playerBarSegment} ${segment.barClass}`}
                  style={{ flex: segment.count }}
                />
              ))}
            </Box>
          </Box>
          <PlayerRosterLegend segments={segments} classes={classes} />
        </Box>
      ) : null}
    </Box>
  );
}

function ArithmeticRow({ line }: { line: ArithmeticLine }) {
  const classes = useStyles();
  const rowClass = line.isTotal ? classes.arithmeticRowTotal : classes.arithmeticRow;
  const opClass = line.isTotal
    ? `${classes.op} ${line.isPlaceholder ? classes.opMuted : classes.opTotal}`
    : line.isDeduction
      ? `${classes.op} ${classes.opDeduction}`
      : line.isPlaceholder
        ? `${classes.op} ${classes.opMuted}`
        : classes.op;
  const labelClass = line.isTotal
    ? classes.lineLabelTotal
    : line.isPlaceholder
      ? `${classes.lineLabel} ${classes.lineLabelMuted}`
      : classes.lineLabel;
  const valueClass = line.isTotal
    ? line.isPlaceholder
      ? `${classes.lineValue} ${classes.lineValueTotal} ${classes.lineValueMuted}`
      : `${classes.lineValue} ${classes.lineValueTotal}`
    : line.isDeduction
      ? `${classes.lineValue} ${classes.lineValueDeduction}`
      : line.isPlaceholder
        ? `${classes.lineValue} ${classes.lineValueMuted}`
        : classes.lineValue;

  return (
    <Box className={rowClass}>
      <Typography component="span" className={opClass}>
        {line.op}
      </Typography>
      <Box className={classes.lineLabelWrap}>
        <Typography component="span" className={labelClass}>
          {line.label}
        </Typography>
        {line.entryAmountPence != null && line.multiplier != null ? (
          <Chip
            size="small"
            className={classes.lineDetailChip}
            label={`${formatGbp(line.entryAmountPence)} ×${line.multiplier}`}
          />
        ) : null}
      </Box>
      <Typography component="span" className={valueClass}>
        {line.isDeduction ? `−${line.value}` : line.value}
      </Typography>
    </Box>
  );
}

export default function GamePaymentSummary({ gameId, refreshKey = 0, alwaysExpanded = false }: Props) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<GamePaymentSummaryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(alwaysExpanded);

  const toggleExpanded = () => {
    if (alwaysExpanded) return;
    setExpanded(prev => !prev);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleExpanded();
    }
  };

  useEffect(() => {
    if (!gameId) return;
    setLoading(true);
    setError(null);
    paymentApi
      .getGamePaymentSummary(gameId)
      .then((res: { data: GamePaymentSummaryData }) => {
        setSummary(res.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.response?.data?.error || "Could not load payment summary");
        setLoading(false);
      });
  }, [gameId, refreshKey]);

  const wrapForDialog = (content: React.ReactElement) =>
    alwaysExpanded ? (
      <Box className={`${classes.root} ${classes.rootDialog}`}>{content}</Box>
    ) : (
      content
    );

  if (loading) {
    const loadingContent = <SummaryLoading dialog={alwaysExpanded} />;
    return alwaysExpanded ? wrapForDialog(loadingContent) : loadingContent;
  }

  if (error) {
    return wrapForDialog(
      <Box className={`${classes.stateBox} ${alwaysExpanded ? classes.stateBoxDialog : ""}`}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>,
    );
  }

  if (!summary) {
    return wrapForDialog(
      <Box className={`${classes.stateBox} ${alwaysExpanded ? classes.stateBoxDialog : ""}`}>
        <Typography variant="body2" color="textSecondary">
          Payment summary unavailable.
        </Typography>
      </Box>,
    );
  }

  const feesPence = summary.schalaFeePence + summary.stripeProcessingPence;
  const hasCard = summary.stripePaymentCount > 0 && summary.cardGrossPence > 0;
  const hasManual = summary.manualPaymentCount > 0 && summary.manualCollectedPence > 0;
  const collected = hasCollections(summary);
  const hasFees = feesPence > 0;
  const arithmeticLines = buildArithmeticLines(summary);
  const showExpanded = alwaysExpanded || expanded;

  const renderSummaryToggle = () => (
    <Box className={classes.summaryToggle}>
      <Typography component="span" className={classes.summaryToggleText}>
        {showExpanded ? "Hide details" : "Show details"}
      </Typography>
      <ChevronRight
        className={`${classes.summaryChevron} ${showExpanded ? classes.summaryChevronExpanded : ""}`}
        aria-hidden
      />
    </Box>
  );

  const renderSummaryHeader = (includeToggle = false) => (
    <SummaryHero
      summary={summary}
      classes={classes}
      dialogMode={alwaysExpanded}
      topRight={includeToggle ? renderSummaryToggle() : undefined}
    />
  );

  const renderPaymentChips = () => {
    if (!collected) {
      return (
        <Box className={classes.chipItemLead}>
          <Chip
            size="small"
            className={`${classes.chip} ${classes.chipFill} ${classes.chipMuted}`}
            label="Nothing collected yet"
          />
        </Box>
      );
    }

    const paidLabel = `${summary.playersPaidCount} paid`;

    if (hasCard && !hasManual) {
      return (
        <Box className={classes.chipItemLead}>
          <Chip
            size="small"
            className={`${classes.chip} ${classes.chipFill}`}
            icon={<CreditCard style={{ fontSize: 16 }} />}
            label={`${paidLabel}, via card`}
          />
        </Box>
      );
    }

    if (hasManual && !hasCard) {
      return (
        <Box className={classes.chipItemLead}>
          <Chip
            size="small"
            className={`${classes.chip} ${classes.chipFill}`}
            icon={<LocalAtm style={{ fontSize: 16 }} />}
            label={`${paidLabel}, manually`}
          />
        </Box>
      );
    }

    return (
      <>
        <Box className={classes.chipItemLead}>
          <Chip
            size="small"
            className={`${classes.chip} ${classes.chipFill}`}
            label={paidLabel}
          />
        </Box>
        <ChipSeparator symbol="=" />
        {hasCard ? (
          <Box className={classes.chipItem}>
            <Chip
              size="small"
              className={classes.chip}
              icon={<CreditCard style={{ fontSize: 16 }} />}
              label={`${summary.stripePaymentCount} card`}
            />
          </Box>
        ) : null}
        {hasCard && hasManual ? <ChipSeparator symbol="+" /> : null}
        {hasManual ? (
          <Box className={classes.chipItem}>
            <Chip
              size="small"
              className={classes.chip}
              icon={<LocalAtm style={{ fontSize: 16 }} />}
              label={`${summary.manualPaymentCount} manual`}
            />
          </Box>
        ) : null}
      </>
    );
  };

  const renderLedgerBody = () => (
    <Box
      className={`${classes.expandedContent}${
        alwaysExpanded ? ` ${classes.expandedContentDialog}` : ""
      }`}
    >
      <Box className={`${classes.chipsBar}${alwaysExpanded ? ` ${classes.chipsBarDialog}` : ""}`}>
        {renderPaymentChips()}
      </Box>

      <Box className={`${classes.section}${alwaysExpanded ? ` ${classes.sectionDialog}` : ""}`}>
        <Box className={classes.arithmetic}>
          {arithmeticLines.map(line => (
            <ArithmeticRow key={`${line.op}-${line.label}`} line={line} />
          ))}
        </Box>
      </Box>

      <Box className={`${classes.footer}${alwaysExpanded ? ` ${classes.footerDialog}` : ""}`}>
        {!collected ? (
          <Typography className={classes.footerText} component="p">
            Collect entry fees from the game Payment menu, or mark players paid off-app once you receive
            cash or bank transfer.
          </Typography>
        ) : null}
        {hasFees ? (
          <Typography className={classes.footerText} component="p">
            Card fees (Schala {formatGbp(summary.schalaFeePence)}
            {summary.stripeProcessingPence > 0
              ? `, Stripe ${formatGbp(summary.stripeProcessingPence)}`
              : ""}
            ) are deducted from card payments only.
          </Typography>
        ) : null}
        <Typography className={classes.footerText} component="p">
          Card payouts are made via your Stripe dashboard. Manual entries record off-app payments marked by
          you — Schala does not process those funds.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box className={`${classes.root} ${alwaysExpanded ? classes.rootDialog : ""}`}>
      {alwaysExpanded ? (
        <>
          <Box
            className={`${classes.summaryHeaderStatic} ${classes.summaryHeaderStaticDialog}`}
          >
            {renderSummaryHeader()}
          </Box>
          {renderLedgerBody()}
        </>
      ) : (
        <>
          <Box
            className={`${classes.summaryTrigger} ${showExpanded ? classes.summaryTriggerExpanded : ""}`}
            onClick={toggleExpanded}
            onKeyDown={handleTriggerKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={showExpanded}
            aria-label={
              showExpanded
                ? `Hide payment breakdown for ${formatGbp(summary.takeHomePence)} collected`
                : `Show payment breakdown for ${formatGbp(summary.takeHomePence)} collected`
            }
          >
            {renderSummaryHeader(true)}
          </Box>

          <Collapse in={showExpanded} timeout={320}>
            {renderLedgerBody()}
          </Collapse>
        </>
      )}
    </Box>
  );
}
